import DOMPurify from "dompurify";

// Force safe rel on any target="_blank" link so sanitized rich-text can't enable
// reverse-tabnabbing (window.opener access). Registered once at module load.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// URL schemes we allow for admin-entered links (What's New body + Events/
// Resources hrefs). Anything else (javascript:, data:, …) is rejected.
const SAFE_URL_SCHEME = /^(https?:|mailto:|tel:|\/|#)/i;

/**
 * Sanitize vendor-authored HTML (What's New body) before it is persisted or
 * rendered. What's New is written by platform admins, but we still never trust
 * raw HTML into the DOM — strip scripts/handlers/unsafe URLs so a stored-XSS
 * payload can't reach a firm user's browser.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html ?? "", {
    ALLOWED_TAGS: [
      "p", "br", "b", "strong", "i", "em", "u", "s", "a", "ul", "ol", "li",
      "h1", "h2", "h3", "h4", "blockquote", "code", "pre", "hr", "span",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    // Block javascript:/data: URLs in links.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/**
 * Guard an admin-entered `href` before rendering it as a live link to firm
 * users. Returns the URL if its scheme is allow-listed, otherwise `#` so a
 * `javascript:`/`data:` value can never become a clickable link.
 */
export function safeUrl(href: string | null | undefined): string {
  const value = (href ?? "").trim();
  if (!value) return "#";
  return SAFE_URL_SCHEME.test(value) ? value : "#";
}
